import { useState, useEffect, useCallback } from 'react';
import Header from '../../components/Header';
import { RecordingPlayer } from '../../components/RecordingPlayer';
import { RecordingOverlay } from '../../components/RecordingOverlay';
import { useRecorder } from '../../hooks/useRecorder';
import { uploadRecording } from '../../lib/uploadRecording';
import { LAYER_LABELS } from '../../types/capsule';
import { parseCourseSubmissionSlug, COURSE_LAYER_LABELS } from '../../types/course';
import type { SubmissionIndexEntry, Submission, Feedback } from '../../types/submission';
import type { LayerName } from '../../types/capsule';
import type { CourseLayerName } from '../../types/course';
import '../../styles/ReviewDashboard.css';

type FilterTab = 'all' | 'pending' | 'reviewed';

// TODO: move to shared utils
function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// TODO: move to shared utils
function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function FeedbackView({ feedback }: { feedback: Feedback }) {
  return (
    <div className="review-feedback-display">
      <div className="review-feedback-display-header">
        <span className="review-feedback-display-title">Feedback Given</span>
        <span className="review-feedback-display-reviewer">
          by {feedback.reviewerEmail} on{' '}
          {formatDate(feedback.createdAt)}
        </span>
      </div>
      <div className="review-feedback-display-text">{feedback.text}</div>
      {feedback.videoUrl && (
        <div style={{ marginTop: 'var(--space-sm)' }}>
          <video
            src={feedback.videoUrl}
            controls
            playsInline
            style={{ width: '100%', maxWidth: 400, maxHeight: 300, borderRadius: 'var(--radius-md)', background: '#000' }}
          />
        </div>
      )}
      {feedback.timestamps && feedback.timestamps.length > 0 && (
        <div className="review-feedback-display-timestamps">
          {feedback.timestamps.map((ts, i) => (
            <div key={i} className="review-feedback-ts-item">
              <span className="review-feedback-ts-time">
                {formatTime(ts.time)}
              </span>
              <span className="review-feedback-ts-comment">
                {ts.comment}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface TimestampNote {
  time: number;
  comment: string;
}

function parseTimeInput(input: string): number | null {
  const parts = input.trim().split(':');
  if (parts.length === 2) {
    const m = parseInt(parts[0], 10);
    const s = parseInt(parts[1], 10);
    if (!isNaN(m) && !isNaN(s) && s >= 0 && s < 60 && m >= 0) {
      return m * 60 + s;
    }
  }
  if (parts.length === 1) {
    const s = parseInt(parts[0], 10);
    if (!isNaN(s) && s >= 0) return s;
  }
  return null;
}

function FeedbackForm({
  submissionId,
  currentPlaybackTime,
  existingFeedback,
  onSubmitted,
}: {
  submissionId: string;
  currentPlaybackTime: number;
  existingFeedback?: Feedback;
  onSubmitted: (submission: Submission) => void;
}) {
  const [text, setText] = useState(existingFeedback?.text || '');
  const [timestamps, setTimestamps] = useState<TimestampNote[]>(
    existingFeedback?.timestamps || []
  );
  const [newComment, setNewComment] = useState('');
  const [timeInput, setTimeInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Video feedback recording
  const recorder = useRecorder();
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [feedbackVideoUrl, setFeedbackVideoUrl] = useState<string | null>(existingFeedback?.videoUrl || null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);

  function handleAddTimestamp() {
    if (!newComment.trim()) return;
    const manualTime = parseTimeInput(timeInput);
    const time = manualTime !== null ? manualTime : currentPlaybackTime;
    setTimestamps([...timestamps, { time, comment: newComment.trim() }]);
    setNewComment('');
    setTimeInput('');
  }

  function handleUsePlaybackTime() {
    setTimeInput(formatTime(currentPlaybackTime));
  }

  function handleRemoveTimestamp(index: number) {
    setTimestamps(timestamps.filter((_, i) => i !== index));
  }

  async function handleVideoSubmit(blob: Blob) {
    setVideoUploading(true);
    setVideoUploadProgress(0);
    try {
      const result = await uploadRecording(blob, `feedback/${submissionId}`, (progress) => {
        setVideoUploadProgress(progress.percent);
      });
      setFeedbackVideoUrl(result.secureUrl);
      setShowVideoRecorder(false);
      recorder.clearRecording();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload video feedback');
    } finally {
      setVideoUploading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim() && !feedbackVideoUrl) return;

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch('/api/submissions/feedback', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submissionId,
          text: text.trim(),
          ...(timestamps.length > 0 && { timestamps }),
          ...(feedbackVideoUrl && { videoUrl: feedbackVideoUrl }),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit feedback');
      }

      setSuccess(true);
      onSubmitted(data.submission);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="review-feedback-success">
        Feedback {existingFeedback ? 'updated' : 'submitted'} successfully.
      </div>
    );
  }

  return (
    <form className="review-feedback-form" onSubmit={handleSubmit}>
      <label className="review-feedback-label">Feedback</label>
      <textarea
        className="review-feedback-textarea"
        placeholder="Write your feedback for the student..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
      />

      <div className="review-feedback-ts-section">
        <label className="review-feedback-label">Timestamp Notes (optional)</label>
        {timestamps.length > 0 && (
          <div className="review-feedback-ts-list">
            {timestamps.map((ts, i) => (
              <div key={i} className="review-feedback-ts-entry">
                <span className="review-feedback-ts-time">
                  {formatTime(ts.time)}
                </span>
                <span className="review-feedback-ts-comment">{ts.comment}</span>
                <button
                  type="button"
                  className="review-feedback-ts-remove"
                  onClick={() => handleRemoveTimestamp(i)}
                  aria-label="Remove timestamp note"
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="review-feedback-ts-add">
          <input
            type="text"
            className="review-feedback-ts-time-input"
            placeholder={formatTime(currentPlaybackTime)}
            value={timeInput}
            onChange={(e) => setTimeInput(e.target.value)}
            title="Type m:ss or click 'Now' to use playback time"
            style={{ width: '60px', textAlign: 'center' }}
          />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={handleUsePlaybackTime}
            title="Use current playback position"
          >
            Now
          </button>
          <input
            type="text"
            className="review-feedback-ts-input"
            placeholder="Note at this timestamp..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTimestamp();
              }
            }}
          />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={handleAddTimestamp}
            disabled={!newComment.trim()}
          >
            Add
          </button>
        </div>
      </div>

      {/* Video feedback section */}
      <div style={{ marginTop: 'var(--space-md)' }}>
        <label className="review-feedback-label">Video Feedback (optional)</label>
        {feedbackVideoUrl && !showVideoRecorder && (
          <div style={{ marginBottom: 'var(--space-sm)' }}>
            <video
              src={feedbackVideoUrl}
              controls
              playsInline
              style={{ width: '100%', maxWidth: 320, maxHeight: 240, borderRadius: 'var(--radius-md)', background: '#000' }}
            />
            <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-xs)' }}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setFeedbackVideoUrl(null); }}>
                Remove video
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowVideoRecorder(true)}>
                Re-record
              </button>
            </div>
          </div>
        )}
        {!feedbackVideoUrl && !showVideoRecorder && (
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => setShowVideoRecorder(true)}
            style={{ marginBottom: 'var(--space-sm)' }}
          >
            Record Video Feedback
          </button>
        )}
        {showVideoRecorder && (
          <div style={{ marginBottom: 'var(--space-sm)', padding: 'var(--space-sm)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
            <RecordingOverlay
              isRecording={recorder.isRecording}
              recordingUrl={recorder.recordingUrl}
              recordingBlob={recorder.recordingBlob}
              error={recorder.error}
              onStartRecording={recorder.startRecording}
              onStopRecording={recorder.stopRecording}
              onClearRecording={recorder.clearRecording}
              onSubmit={handleVideoSubmit}
              onClose={() => { setShowVideoRecorder(false); recorder.clearRecording(); }}
              isUploading={videoUploading}
              uploadProgress={videoUploadProgress}
              stream={recorder.stream}
              hasVideo={recorder.hasVideo}
              submitLabel="Save Feedback Video"
            />
          </div>
        )}
      </div>

      {error && <div className="review-feedback-error">{error}</div>}

      <button
        type="submit"
        className="btn btn-primary"
        disabled={submitting || (!text.trim() && !feedbackVideoUrl)}
      >
        {submitting ? (existingFeedback ? 'Updating...' : 'Submitting...') : (existingFeedback ? 'Update Feedback' : 'Submit Feedback')}
      </button>
    </form>
  );
}

function ReviewCard({
  entry,
  onUpdated,
}: {
  entry: SubmissionIndexEntry;
  onUpdated: (id: string, submission: Submission) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<Submission | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editing, setEditing] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const courseInfo = parseCourseSubmissionSlug(entry.capsuleSlug);
  const isCourse = courseInfo !== null;

  const layerLabel = isCourse
    ? (COURSE_LAYER_LABELS[entry.layerName as CourseLayerName] || entry.layerName)
    : (LAYER_LABELS[entry.layerName as LayerName] || entry.layerName);

  async function handleToggle() {
    if (!expanded) {
      // Always re-fetch to get latest data (e.g., newly added video feedback)
      setLoadingDetail(true);
      try {
        const res = await fetch(`/api/submissions/${entry.id}`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setDetail(data.submission);
        }
      } catch {
        // Failed to load detail
      } finally {
        setLoadingDetail(false);
      }
    }
    setExpanded(!expanded);
  }

  const handleTimeUpdate = useCallback((time: number) => {
    setCurrentTime(time);
  }, []);

  function handleFeedbackSubmitted(updatedSubmission: Submission) {
    setDetail(updatedSubmission);
    onUpdated(entry.id, updatedSubmission);
  }

  return (
    <div className="review-card">
      <div className="review-card-header" onClick={handleToggle}>
        <div className="review-card-info">
          <div className="review-card-student">{entry.userId}</div>
          <div className="review-card-meta">
            <span className="review-card-capsule">
              {isCourse && <span style={{ fontSize: 10, color: 'var(--color-accent)', marginRight: 4 }}>Course:</span>}
              {isCourse ? courseInfo.capsuleSlug : entry.capsuleSlug}
            </span>
            <span className="review-card-layer">{layerLabel}</span>
            <span className="review-card-date">
              {formatDate(entry.createdAt)}
            </span>
          </div>
        </div>

        <span
          className={`submission-status ${
            entry.status === 'reviewed'
              ? 'submission-status-reviewed'
              : 'submission-status-pending'
          }`}
        >
          {entry.status === 'reviewed' ? 'Reviewed' : 'Pending'}
        </span>

        <span
          className={`review-card-expand ${expanded ? 'expanded' : ''}`}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="4 6 8 10 12 6" />
          </svg>
        </span>
      </div>

      {expanded && (
        <div className="review-card-detail">
          {loadingDetail ? (
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              Loading submission...
            </p>
          ) : detail ? (
            <>
              <RecordingPlayer
                src={detail.recordingUrl}
                timestamps={detail.feedback?.timestamps}
                onTimeUpdate={handleTimeUpdate}
              />

              {detail.note && (
                <div className="review-student-note">
                  <div className="review-student-note-label">Student Note</div>
                  {detail.note}
                </div>
              )}

              {detail.feedback && !editing ? (
                <>
                  <FeedbackView feedback={detail.feedback} />
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setEditing(true)}
                    style={{ marginTop: 8 }}
                  >
                    Edit Feedback
                  </button>
                </>
              ) : (
                <FeedbackForm
                  key={editing ? 'edit' : 'new'}
                  submissionId={entry.id}
                  currentPlaybackTime={currentTime}
                  existingFeedback={detail.feedback}
                  onSubmitted={(updated) => {
                    handleFeedbackSubmitted(updated);
                    setEditing(false);
                  }}
                />
              )}
            </>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              Could not load submission details.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function ReviewDashboard() {
  const [submissions, setSubmissions] = useState<SubmissionIndexEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterTab>('all');

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch('/api/submissions/list?all=true', {
          credentials: 'include',
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Failed to fetch submissions (${res.status})`);
        }
        const data = await res.json();
        if (!cancelled) {
          setSubmissions(data.submissions || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load submissions');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, []);

  function handleSubmissionUpdated(id: string, updated: Submission) {
    setSubmissions(prev =>
      prev.map(s => (s.id === id ? { ...s, status: updated.status } : s))
    );
  }

  const filtered = filter === 'all'
    ? submissions
    : submissions.filter(s => s.status === filter);

  const pendingCount = submissions.filter(s => s.status === 'pending').length;
  const reviewedCount = submissions.filter(s => s.status === 'reviewed').length;

  return (
    <div className="review-page">
      <Header />
      <div className="review-content">
        <div className="review-header">
          <h1>Submission Reviews</h1>
          <p>Review student recordings and provide feedback</p>
        </div>

        <div className="review-tabs">
          <button
            className={`review-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({submissions.length})
          </button>
          <button
            className={`review-tab ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending ({pendingCount})
          </button>
          <button
            className={`review-tab ${filter === 'reviewed' ? 'active' : ''}`}
            onClick={() => setFilter('reviewed')}
          >
            Reviewed ({reviewedCount})
          </button>
        </div>

        {loading && (
          <div className="review-loading">Loading submissions...</div>
        )}

        {error && <div className="review-error">{error}</div>}

        {!loading && !error && filtered.length === 0 && (
          <div className="review-empty">
            {filter === 'all'
              ? 'No submissions yet.'
              : `No ${filter} submissions.`}
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="review-list">
            {filtered.map(entry => (
              <ReviewCard
                key={entry.id}
                entry={entry}
                onUpdated={handleSubmissionUpdated}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
